import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  UseQueryOptions
} from "@tanstack/react-query";

import {
  AddOrganizationUserRequest,
  AddOrganizationUserResponse,
  GetUserByIdDetail,
  GetUsersPayload,
  GetUsersResponse,
  PatchUserPayload
} from "@/types/user";
import { CustomError } from "@/utils/errors";

import {
  deleteOrganizationUser,
  getUser,
  getUsers,
  patchUser,
  postOrganizationUser,
  postResendActivation
} from "./userApi";

interface CreateOrganizationUserMutationParams {
  userData: AddOrganizationUserRequest;
}

export const useGetUserByIdAPI = (
  id: string,
  options?: Omit<
    UseQueryOptions<GetUserByIdDetail, Error, GetUserByIdDetail>,
    "queryKey" | "queryFn"
  >
) =>
  useQuery({
    queryKey: ["getUserById", id],
    queryFn: () => getUser(id),
    ...options
  });

export const GET_USERS_DEFAULT_PARAMS = {
  query: null,
  status: null,
  sort_by: null,
  order_by: null,
  group_id: null,
  page: 1,
  per_page: 10
};

export const useGetUsersAPI = (
  {
    query,
    status,
    order_by,
    sort_by,
    page,
    per_page,
    group_id
  }: GetUsersPayload = GET_USERS_DEFAULT_PARAMS,
  options?: Omit<
    UseQueryOptions<GetUsersResponse, Error, GetUsersResponse>,
    "queryKey" | "queryFn"
  >
) =>
  useQuery({
    queryKey: [
      "getUsers",
      query,
      status,
      sort_by,
      order_by,
      page,
      per_page,
      group_id
    ],
    queryFn: () =>
      getUsers({
        query,
        status,
        order_by,
        sort_by,
        page,
        per_page,
        group_id
      }),
    ...options
  });

export const useCreateOrganizationUser = (
  options?: UseMutationOptions<
    AddOrganizationUserResponse,
    CustomError,
    CreateOrganizationUserMutationParams
  >
): UseMutationResult<
  AddOrganizationUserResponse,
  CustomError,
  CreateOrganizationUserMutationParams
> => {
  return useMutation({
    mutationFn: ({ userData }) => postOrganizationUser(userData),
    ...options
  });
};

export const useResendActivationMutation = (
  options?: UseMutationOptions<null, CustomError, string>
) =>
  useMutation({
    mutationFn: (userId) => postResendActivation(userId),
    ...options
  });

export const useDeleteOrganizationUser = (
  options?: UseMutationOptions<null, CustomError, string>
) =>
  useMutation({
    mutationFn: (userId) => deleteOrganizationUser(userId),
    ...options
  });

export const usePatchUser = (
  options?: UseMutationOptions<
    null,
    { message: CustomError },
    { userId: string; userData: Partial<PatchUserPayload> }
  >
) =>
  useMutation({
    mutationFn: ({ userId, userData }) => patchUser(userId, userData),
    ...options
  });
